/*
 * Vivalyo — Bundle Guard
 *
 * Quand le client réduit la quantité de GrillMeister Pro, deux choses doivent
 * se produire pour que le panier reste cohérent :
 *
 *  1. RÉALIGNER L'OFFRE quand on retombe à un seul set. La remise à 100 % est
 *     pilotée par l'attribut de ligne `__rapi_offer`, PAS par le contenu du
 *     panier. Tant qu'une ligne du bundle reste marquée sur l'offre « 2 sets »,
 *     l'app ne reconnaît pas un bundle valide et les accessoires repassent
 *     plein tarif (panier à 104 € au lieu de 69 €).
 *
 *     Deux pitiés apprises à la dure :
 *       - il faut réécrire TOUTES les lignes du bundle, produit principal
 *         compris ; ne corriger que les cadeaux ne suffit pas ;
 *       - il faut le faire UNE LIGNE À LA FOIS, en relisant le panier entre
 *         chaque passe. Enchaînées sans délai sur des clés capturées d'avance,
 *         les écritures répondent 200 mais sont perdues.
 *
 *  2. PLAFONNER les accessoires : la quantité TOTALE de chaque accessoire est
 *     ramenée à la quantité totale de GrillMeister restante. Le plafond est
 *     calculé par PRODUIT, pas par ligne — un accessoire peut occuper plusieurs
 *     lignes, et un plafond par ligne laisserait un doublon plein tarif.
 */
(function () {
  const MAIN_PRODUCT_ID = 15627857363321; // Vivalyo GrillMeister Pro™
  const GIFT_PRODUCT_IDS = [
    15629333660025, // USB-C Schnellladegerät
    15629333430649, // Finishing-Bürste
    15629333234041, // Schnittschutzhandschuh
  ];

  /*
   * Code de l'offre « 1 set complet à 69 € » dans rapi-cart.
   * À METTRE À JOUR si l'offre est recréée dans l'app : son code change alors,
   * et sans ça les accessoires repasseraient plein tarif silencieusement.
   * Pour le relire : ajouter le bundle 69 € au panier, puis dans la console
   *   fetch('/cart.js').then(r=>r.json()).then(c=>console.log(c.items.map(i=>i.properties.__rapi_offer)))
   */
  const SINGLE_SET_OFFER = 'kiOQr';

  const MAX_PASSES = 8; // garde-fou contre toute boucle infinie

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const getCart = async () => {
    const r = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } });
    return r.json();
  };

  const offerOf = (item) => (item.properties && item.properties.__rapi_offer) || '';

  const isBundleLine = (item) =>
    item.product_id === MAIN_PRODUCT_ID || GIFT_PRODUCT_IDS.indexOf(item.product_id) !== -1;

  // Remplace uniquement le champ `offer`, en préservant `id`, `main` et `gift`.
  // Renvoie null si la ligne est déjà sur la bonne offre ou illisible.
  function realignedOffer(raw) {
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.offer === SINGLE_SET_OFFER) return null;
      parsed.offer = SINGLE_SET_OFFER;
      return JSON.stringify(parsed);
    } catch (e) {
      return null;
    }
  }

  async function setLineOffer(key, value) {
    const r = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, properties: { __rapi_offer: value } }),
    });
    return r.ok;
  }

  /*
   * Réaligne tout le bundle sur l'offre « 1 set », une ligne par passe.
   * Renvoie true si au moins une ligne a été modifiée.
   */
  async function realignBundle() {
    let touched = false;

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      const cart = await getCart();
      if (!cart || !Array.isArray(cart.items)) return touched;

      let cible = null;
      let valeur = null;

      for (const item of cart.items) {
        if (!isBundleLine(item)) continue;
        const v = realignedOffer(offerOf(item));
        if (v) { cible = item; valeur = v; break; }
      }

      if (!cible) return touched; // plus rien à corriger

      await setLineOffer(cible.key, valeur);
      touched = true;
      await sleep(400); // laisser Shopify committer avant la passe suivante
    }

    return touched;
  }

  /*
   * Renvoie un tableau de quantités pour /cart/update.js, ou null s'il n'y a
   * rien de particulier à faire (le thème suit alors son chemin habituel).
   * `line` est l'index 1-based que le thème s'apprête à mettre à `quantity`.
   */
  async function buildUpdates(line, quantity) {
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      console.error('[bundle-guard]', e);
      return null; // on ne bloque jamais le panier
    }
    if (!cart || !Array.isArray(cart.items)) return null;

    const index = Number(line) - 1;
    const target = cart.items[index];

    // On n'intervient que si le client touche au produit principal.
    if (!target || target.product_id !== MAIN_PRODUCT_ID) return null;

    const targetKey = target.key;
    const nextQuantity = Math.max(0, Number(quantity) || 0);

    const mainTotal = cart.items.reduce((sum, item, i) => {
      if (item.product_id !== MAIN_PRODUCT_ID) return sum;
      return sum + (i === index ? nextQuantity : item.quantity);
    }, 0);

    // Étape 1 — uniquement quand on retombe à EXACTEMENT un set.
    let realigned = false;
    if (mainTotal === 1) {
      try {
        realigned = await realignBundle();
      } catch (e) {
        console.error('[bundle-guard]', e);
      }
      if (realigned) cart = await getCart();
    }

    // Étape 2 — plafonner les quantités, sur le panier à jour.
    let mainIndex = cart.items.findIndex((item) => item.key === targetKey);
    if (mainIndex === -1) {
      // La clé a changé avec la réécriture : on retrouve la ligne par produit.
      mainIndex = cart.items.findIndex((item) => item.product_id === MAIN_PRODUCT_ID);
    }
    if (mainIndex === -1) return null;

    const allowance = {};
    GIFT_PRODUCT_IDS.forEach((id) => { allowance[id] = mainTotal; });

    let capped = false;
    const updates = cart.items.map((item, i) => {
      if (i === mainIndex) return nextQuantity;
      if (!Object.prototype.hasOwnProperty.call(allowance, item.product_id)) return item.quantity;
      const allowed = Math.min(item.quantity, allowance[item.product_id]);
      allowance[item.product_id] -= allowed;
      if (allowed !== item.quantity) capped = true;
      return allowed;
    });

    /*
     * Si on a réécrit des offres, on renvoie toujours le tableau complet :
     * l'index de ligne que détient le thème est périmé, et un /cart/change.js
     * sur un mauvais index toucherait la mauvaise ligne.
     */
    return capped || realigned ? updates : null;
  }

  window.VivalyoBundleGuard = {
    buildUpdates,
    updateUrl: () => root() + 'cart/update.js',
  };
})();
